import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Package, Clock, TrendingUp, Wallet, Inbox, CalendarClock, Camera, FileDown, SlidersHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pdf, setPdf] = useState(false);

  useEffect(() => {
    api.get("/units/dashboard/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const openPdf = async () => {
    setPdf(true);
    try {
      const res = await api.get("/in-stock-pdf", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiError(e)); } finally { setPdf(false); }
  };

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2997ff]" /></div>;

  const tiles = [
    { label: "In stock", value: stats.in_stock, icon: Package, color: "#30d158", to: "/?status=In stock", testid: "tile-in-stock" },
    { label: "Reserved", value: stats.reserved, icon: Clock, color: "#ff9f0a", to: "/?status=Reserved", testid: "tile-reserved" },
    { label: "Sold this week", value: stats.sold_week, icon: TrendingUp, color: "#2997ff", to: "/?status=Sold", testid: "tile-sold-week" },
    { label: "New enquiries", value: stats.new_enquiries, icon: Inbox, color: "#bf5af2", to: "/enquiries?status=New", testid: "tile-new-enquiries" },
    { label: "Follow-ups due", value: stats.followups_due, icon: CalendarClock, color: "#ff375f", to: "/enquiries?due=1", testid: "tile-followups" },
  ];

  return (
    <div className="mf-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-sm text-[#98989d]">Welcome, {user?.name}</p>
        </div>
        {user?.role === "owner" && (
          <button data-testid="owner-settings-button" onClick={() => navigate("/settings/owner")} className="flex items-center gap-2 rounded-full border border-white/10 bg-[#161618] px-4 py-2 text-sm font-semibold active:scale-95">
            <SlidersHorizontal size={15} /> Owner settings
          </button>
        )}
      </div>

      {/* primary actions */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <button data-testid="dash-add-mac" onClick={() => navigate("/add")} className="flex items-center justify-center gap-2 rounded-2xl bg-[#2997ff] py-4 font-semibold active:scale-[0.98]">
          <Camera size={18} /> Add Mac
        </button>
        <button data-testid="dash-pdf" onClick={openPdf} disabled={pdf} className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#161618] py-4 font-semibold active:scale-[0.98]">
          {pdf ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />} In-Stock PDF
        </button>
      </div>

      {/* stock value */}
      <button data-testid="tile-stock-value" onClick={() => navigate("/?status=In stock")} className="relative mb-4 w-full overflow-hidden rounded-3xl border border-white/10 bg-[#121214] p-6 text-left active:scale-[0.99]">
        <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-[#30d158] opacity-20 blur-3xl" />
        <div className="relative flex items-center gap-2 text-sm text-[#98989d]"><Wallet size={16} /> Stock value (in-stock)</div>
        <div className="relative mt-2 text-4xl font-extrabold tracking-tight text-[#30d158]">{inr(stats.stock_value) || "₹0"}</div>
        <div className="relative mt-1 text-xs text-[#98989d]">Sold this month: {inr(stats.sold_month_value) || "₹0"} · {stats.total_units} total units</div>
      </button>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.label} data-testid={t.testid} onClick={() => navigate(t.to)} className="rounded-2xl border border-white/10 bg-[#121214] p-4 text-left active:scale-[0.97]">
              <Icon size={18} style={{ color: t.color }} />
              <div className="mt-3 text-3xl font-extrabold tracking-tight">{t.value}</div>
              <div className="text-xs text-[#98989d]">{t.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
